import React, { memo, useEffect, useMemo, useState } from "react";
import { Box } from "@material-ui/core";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
    stateTreeBadgeStyle,
    stateTreeCardBaseStyle,
    themePalette,
} from "../Dashboards/State/stateTreeStyles";
import "./styles.css";

const normalizeNodeText = (value) =>
    String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();

const formatHierarchyBadgeLabel = (value) =>
    String(value || "")
        .split(" ")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

const OrderEditRow = memo(function OrderEditRow({ node }) {
    const normalizedLevelName = useMemo(
        () => normalizeNodeText(node?.level_nombre),
        [node?.level_nombre]
    );
    const levelBadgeLabel = useMemo(
        () => formatHierarchyBadgeLabel(node?.level_nombre),
        [node?.level_nombre]
    );

    return (
        <Box
            style={{
                ...stateTreeCardBaseStyle,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "0.75rem",
                minHeight: 48,
                padding: "0.36rem 0.72rem",
                borderColor: "rgba(180, 83, 9, 0.24)",
                background: "linear-gradient(180deg, rgba(255, 247, 237, 0.98) 0%, rgba(255, 237, 213, 0.98) 100%)",
                boxShadow: "inset 0 0 0 1px rgba(255, 255, 255, 0.55)",
                boxSizing: "border-box",
            }}
        >
            <Box
                style={{
                    minWidth: 0,
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: "0.55rem",
                    overflow: "hidden",
                }}
            >
                <Box
                    style={{
                        flexShrink: 0,
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: node?.color || themePalette.primary.main,
                        border: `2px solid ${themePalette.common.white}`,
                        boxShadow: `0 0 0 1px ${themePalette.border.main}, 0 3px 8px rgba(15, 23, 42, 0.08)`,
                    }}
                />

                <span
                    style={{
                        ...stateTreeBadgeStyle,
                        backgroundColor:
                            normalizedLevelName === "empresa"
                                ? "rgba(8, 95, 145, 0.08)"
                                : stateTreeBadgeStyle.backgroundColor,
                        borderColor:
                            normalizedLevelName === "empresa"
                                ? "rgba(8, 95, 145, 0.18)"
                                : stateTreeBadgeStyle.borderColor,
                        color:
                            normalizedLevelName === "empresa"
                                ? themePalette.text.section
                                : stateTreeBadgeStyle.color,
                    }}
                >
                    {levelBadgeLabel}
                </span>

                <span
                    style={{
                        minWidth: 0,
                        flex: 1,
                        fontSize: "0.96rem",
                        fontWeight: 400,
                        color: themePalette.primary.main,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {node?.nombre}
                </span>
            </Box>

            <Box
                style={{
                    flexShrink: 0,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#b45309",
                }}
            >
                <DragIndicatorIcon />
            </Box>
        </Box>
    );
});

function numericArray(max) {
    const result = [];
    for (let index = 0; index < max; index += 1) {
        result.push(index.toString());
    }
    return result;
}

function DragDropList({ setFlagEdit, array }) {
    const defaultList = useMemo(() => numericArray(array?.length || 0), [array?.length]);
    const [itemList, setItemList] = useState(defaultList);

    useEffect(() => {
        setItemList(defaultList);
    }, [defaultList]);

    useEffect(() => {
        setFlagEdit((currentFlagEdit) => ({
            ...currentFlagEdit,
            position_children: itemList,
        }));
    }, [itemList, setFlagEdit]);

    const handleDrop = (droppedItem) => {
        if (!droppedItem.destination) return;

        const updatedList = [...itemList];
        const [reorderedItem] = updatedList.splice(droppedItem.source.index, 1);
        updatedList.splice(droppedItem.destination.index, 0, reorderedItem);
        setItemList(updatedList);
    };

    return (
        <div className="App">
            <DragDropContext onDragEnd={handleDrop}>
                <Droppable droppableId="list-container">
                    {(provided) => (
                        <div
                            className="list-container"
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                        >
                            {itemList.map((item, index) => {
                                const node = array[parseInt(item, 10)];
                                if (!node) return null;

                                return (
                                    <Draggable key={item} draggableId={item} index={index}>
                                        {(draggableProvided) => (
                                            <div
                                                className="item-container"
                                                ref={draggableProvided.innerRef}
                                                {...draggableProvided.dragHandleProps}
                                                {...draggableProvided.draggableProps}
                                            >
                                                <div className="container-nodes">
                                                    <OrderEditRow node={node} />
                                                </div>
                                            </div>
                                        )}
                                    </Draggable>
                                );
                            })}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </div>
    );
}

export default memo(DragDropList);
